import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import { get } from "../../lib/request"
import { normalizeIdList } from "./fetchCacheUtils"

export function buildMessagesFetchKey({
  page = 0,
  size = 25,
  sort = "source_timestamp,DESC",
  clientIds = [],
  socialNetworkTypeIds = [],
  geographyCityIds = [],
  messageTypes = [],
  sentiment,
  query,
  dateRange,
  triage,
  view,
} = {}) {
  return JSON.stringify({
    page,
    size,
    sort,
    clientIds: normalizeIdList(clientIds),
    socialNetworkTypeIds: normalizeIdList(socialNetworkTypeIds),
    geographyCityIds: normalizeIdList(geographyCityIds),
    messageTypes: [...(messageTypes || [])].map(String).sort(),
    sentiment: sentiment ?? "all",
    query: query ?? "",
    dateRange: dateRange ?? "all",
    triage: triage ?? "",
    view: view ?? "",
  })
}

function messageRowId(m) {
  return m?.id != null && m.id !== '' ? String(m.id) : ''
}

function uniqueMessagesById(messages) {
  const seen = new Set()
  const out = []
  for (const m of messages || []) {
    const k = messageRowId(m)
    if (!k || seen.has(k)) continue
    seen.add(k)
    out.push(m)
  }
  return out
}

// GET /api/v1/messages
// export const fetchMessages = createAsyncThunk(
//   "messages/fetch",
//   async (
//     { page = 0, size = 25, sort = "source_timestamp,DESC" } = {},
//     { rejectWithValue, getState }
//   ) => {
//     try {
//       const state = getState()
//       const token = state.auth?.token

//       if (!token) {
//         throw new Error("Authentication required")
//       }

//       const query = `messages?page=${page}&size=${size}&sort=${encodeURIComponent(sort)}`
//       const data = await get(query)

//       return data
//     } catch (err) {
//       return rejectWithValue(err?.message || "Failed to fetch messages")
//     }
//   }
// )
export const fetchMessages = createAsyncThunk(
  "messages/fetch",
  async (
    {
      page = 0,
      size = 25,
      sort = "source_timestamp,DESC",
      clientIds = [],
      socialNetworkTypeIds = [],
      geographyCityIds = [],
      messageTypes = [],
      sentiment,
      query,
      dateRange,
      triage,
      view,
    } = {},
    { rejectWithValue, getState }
  ) => {
    try {
      const state = getState()
      const token = state.auth?.token

      if (!token) {
        throw new Error("Authentication required")
      }

      const params = new URLSearchParams()

      params.append("page", page)
      params.append("size", size)
      params.append("sort", sort)

      if (clientIds?.length > 0) {
        clientIds.forEach(id => params.append("client_ids[]", id))
      }

      if (socialNetworkTypeIds?.length > 0) {
        socialNetworkTypeIds.forEach(id => params.append("social_network_type_ids[]", id))
      }

      if (geographyCityIds?.length > 0) {
        geographyCityIds.forEach(id => params.append("geography_city_ids[]", id))
      }

      if (messageTypes?.length > 0) {
        messageTypes.forEach((type) => {
          const value = String(type || '').toLowerCase().trim()
          if (value) params.append('message_types[]', value)
        })
      }

      // ✅ sentiment
      if (sentiment && sentiment !== "all") {
        params.append("sentiment", sentiment.toUpperCase())
      }

      // ✅ search
      if (query) {
        params.append("query", query)
      }

      // ✅ date range
      if (dateRange && dateRange !== "all") {
        const map = {
          today: "TODAY",
          last_7: "LAST_7_DAYS",
          last_10: "LAST_10_DAYS",
          last_30: "LAST_30_DAYS",
          last_90: "LAST_90_DAYS",
          all: "ALL_TIME"
        }
        params.append("date_range", map[dateRange])
      }

      if (triage) {
        params.append("triage", triage)
      }

      if (view) {
        params.append("view", view)
      }

      const data = await get(`messages?${params.toString()}`)

      return data
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to fetch messages")
    }
  },
  {
    condition: (arg = {}, { getState }) => {
      const page = arg.page ?? 0
      if (page !== 0) return true
      const key = buildMessagesFetchKey(arg)
      const s = getState().messages
      if (s.status === "loading" && s.lastFetchKey === key) return false
      return true
    },
  },
)


const initialState = {
  list: [],          // messages content (flattened from page.content)
  page: 0,
  size: 25,
  totalElements: 0,
  totalPages: 0,
  status: "idle",    // idle | loading | succeeded | failed
  error: null,
  hasMore: true,
  /** Latest GET /messages `counts` (inbox/archive/triage totals); stable across scroll pages. */
  counts: null,
  lastFetchKey: null,
}

const messagesSlice = createSlice({
  name: "messages",
  initialState,
  reducers: {
    clearMessages: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMessages.pending, (state, action) => {
        state.status = "loading"
        state.error = null
        if ((action.meta.arg?.page ?? 0) === 0) {
          state.lastFetchKey = buildMessagesFetchKey(action.meta.arg)
        }
      })
      .addCase(fetchMessages.fulfilled, (state, action) => {
        state.status = "succeeded"

        const payload = action.payload
        if (payload?.counts != null && typeof payload.counts === "object") {
          state.counts = payload.counts
        }

        // New envelope: { messages: SpringPage, counts? } — legacy: SpringPage at root
        const page = payload?.messages != null ? payload.messages : payload

        if (page?.content) {
          const newMessages = page.content
          const pageNumber =
            page.pageable?.pageNumber ?? page.number ?? 0

          // ✅ FIRST PAGE → replace
          if (pageNumber === 0) {
            state.list = uniqueMessagesById(newMessages)
          }
          // ✅ NEXT PAGES → append (skip ids already on the list — poll page-0 + stale load-more overlap)
          else {
            const seen = new Set(state.list.map(messageRowId).filter(Boolean))
            for (const m of newMessages) {
              const k = messageRowId(m)
              if (!k || seen.has(k)) continue
              seen.add(k)
              state.list.push(m)
            }
          }

          state.page = pageNumber
          state.size = page.pageable?.pageSize ?? page.size ?? 25
          state.totalElements = page.totalElements ?? state.totalElements ?? 0
          state.totalPages = page.totalPages ?? state.totalPages ?? 0

          const lastFlag = page.last
          const totalElements = page.totalElements ?? 0
          // Empty `content` is not always "no data": e.g. after bulk archive, page 0 may be empty while
          // totalElements still reflects remaining inbox rows — must allow paging forward (scroll can't if list is blank).
          if (newMessages.length === 0) {
            if (typeof lastFlag === "boolean") {
              state.hasMore = !lastFlag
            } else if (totalElements > 0) {
              state.hasMore = state.list.length < totalElements
            } else {
              state.hasMore = false
            }
          } else if (typeof lastFlag === "boolean") {
            state.hasMore = !lastFlag
          } else if (totalElements > 0) {
            state.hasMore = state.list.length < totalElements
          } else {
            const pageSize = page.pageable?.pageSize ?? page.size ?? state.size ?? 25
            state.hasMore = newMessages.length >= pageSize
          }
        } else {
          state.list = uniqueMessagesById(Array.isArray(payload) ? payload : [])
          state.hasMore = false
        }
      })

      .addCase(fetchMessages.rejected, (state, action) => {
        state.status = "failed"
        state.error = action.payload
      })
  },
})

export const { clearMessages } = messagesSlice.actions
export default messagesSlice.reducer
